import { ContentTypes } from "@/types/ContentTypes";
import { encode } from "@/utils/stringUtils";
import { axiosCMS, axiosCMSnoAuth } from "./fetchers";
import axios from "axios";

const getHeader = () => {
  const currentUser = localStorage.getItem(
    process.env.NEXT_PUBLIC_USER_KEY || ""
  );
  if (!currentUser) throw new Error("User not authenticated");
  return {
    Authorization: `Bearer ${currentUser}`,
  };
};

export async function getProgrammingLanguages() {
  const { data } = await axiosCMS.get("/api/programming-languages", {
    params: {
      fields: ["name", "codeHighlightName", "enabled"],
      filters: {
        enabled: {
          $eq: true,
        },
      },
    },
  });
  return data;
}

export interface PaginatedParams {
  key: string;
  pagination: {
    page: number;
    pageSize: number;
  };
  sort: string[];
}

export async function getExercises({ key, pagination, sort }: PaginatedParams) {
  const { data } = await axiosCMS.get("/api/code-exercises", {
    params: {
      pagination,
      sort,
      populate: ["techStack"],
    },
  });
  return data;
}

export async function getBlogPosts({ key, pagination, sort }: PaginatedParams) {
  const { data } = await axiosCMS.get("/api/blog-posts", {
    params: {
      pagination,
      sort,
      populate: {
        author: {
          fields: ["firstName", "lastName", "username"],
          populate: ["profilePhoto"],
        },
        image: {
          fields: "*",
        },
      },
    },
  });
  return data;
}

export async function getCourses({ key, pagination, sort }: PaginatedParams) {
  const { data } = await axiosCMS.get("/api/courses", {
    params: {
      pagination,
      sort,
      populate: {
        image: "*",
      },
    },
  });
  return data;
}

export async function getContentTypeBySlug({
  contentType,
  slug,
  populate = [],
}: {
  contentType: string;
  slug: string;
  populate?: string[];
}) {
  const response = await axiosCMS.get(
    `api/slugify/slugs/${contentType}/${slug}`,
    {
      params: {
        populate: [
          "seo",
          "seo.socials",
          "seo.image",
          "seo.socials.image",
          ...populate,
        ],
      },
    }
  );
  return response.data;
}

export async function getBlogPostBySlug({ slug }: { slug: string }) {
  const response = await axiosCMS.get(`api/slugify/slugs/blog-post/${slug}`, {
    params: {
      populate: ["seo", "seo.socials", "seo.image", "seo.socials.image"],
    },
  });
  return response.data;
}

export async function getMarketingPageBySlug({ slug }: { slug: string }) {
  const response = await axiosCMS.get(
    `api/slugify/slugs/marketing-page/${slug}`,
    {
      params: {
        populate: {
          content: {
            on: {
              "page.features": {
                populate: ["features", "features.media"],
              },
              "shared.hero": {
                populate: "*",
              },
              "shared.section-title": {
                populate: "*",
              },
            },
          },
          seo: {
            populate: ["socials", "socials.image", "image"],
          },
        },
      },
    }
  );
  return response.data.data;
}

export async function getExerciseBySlug({ slug }: { slug: string }) {
  const response = await axiosCMS.get(
    `api/slugify/slugs/code-exercise/${slug}`,
    {
      params: {
        populate: [
          "seo",
          "seo.socials",
          "seo.image",
          "seo.socials.image",
          "prompts",
          "prompts.programmingLanguage",
          "users",
        ],
      },
    }
  );
  return response.data;
}

export async function getSolutionById({ id }: { id: number }) {
  const response = await axiosCMS.get(`api/exercise-solutions/${id}`);
  return response?.data;
}

export async function findByExerciseIdAndUserId({
  exerciseId,
}: {
  exerciseId: number;
}) {
  const response = await axios.get(
    `${process.env.NEXT_PUBLIC_CMS_URL}/api/exercise-solutions/exercise`,
    {
      headers: getHeader(),
    }
  );
  return response;
}

export async function runCode({
  id,
  context,
  code,
  languageId,
  meta,
}: {
  code: string;
  languageId: number;
  id: number;
  context: string;
  meta?: Record<string, any>;
}) {
  const encoder = new TextEncoder();
  const toEncode = encoder.encode(code);
  // @ts-ignore
  const sourceCode = btoa(String.fromCharCode(...new Uint8Array(toEncode)));

  const { data } = await axiosCMS.post("/api/code-runner/run", {
    sourceCode: encode(code || ""),
    languageId,
    id,
    context,
    meta,
  });
  return data;
}

export async function getSlugsForContentType(type: ContentTypes) {
  try {
    const response = await axiosCMS.get(`api/${type}/slugs`);
    return response.data.data.map((entry: any) => entry.slug);
  } catch (err) {
    return [];
  }
}

export async function getSolutionsByExerciseId(exercise: number) {
  try {
    const response = await axiosCMS.get(
      `api/${ContentTypes.EXERCISE_SOLUTION}/exercise?exerciseId=${exercise}`
    );
    return response.data;
  } catch (err) {
    return [];
  }
}
